import { Pen, Trash } from "lucide-react";

const ConfigBadge = ({ children, color = "gray" }) => {
  const styles = {
    gray: "bg-gray-100 text-gray-600",
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-700",
    red: "bg-red-100 text-red-700",
    purple: "bg-purple-100 text-purple-700",
    orange: "bg-orange-100 text-orange-700",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[color]}`}
    >
      {children}
    </span>
  );
};

const NumberChip = ({ value, prefix = "", fallback = "-" }) => (
  <span className="inline-flex min-w-[44px] items-center justify-center rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">
    {value === null || value === undefined || value === ""
      ? fallback
      : `${prefix}${value}`}
  </span>
);

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

export default function DriverConfigTable({
  rows,
  onEdit,
  onDelete,
  deletingId,
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div
        className="w-full min-w-0 overflow-auto"
        style={{ height: "calc(100vh - 300px)" }}
      >
        <table className="min-w-full">
          <thead className=" sticky top-0 z-20 border-b border-gray-200 bg-[#2EC8F1]">
            <tr>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Config ID
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Driver Code
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-white">
                Driver Name
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Team
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Team ID
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Driver Type
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Tier
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                KYC
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                On Duty
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Cash Limit
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Daily Task
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Concurrent
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Max Team
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Per Day
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Per Order
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Created
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">
            {(rows || []).map((r) => (
              <tr
                key={r.pk_config_id}
                className="text-sm transition-colors hover:bg-gray-50"
              >
                <td className="px-4 py-3 text-center">
                  <span className="font-medium text-gray-700">
                    {r.pk_config_id}
                  </span>
                </td>

                <td className="px-4 py-3 text-center">
                  <span className="inline-flex rounded-md bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                    {r.driver_code || "-"}
                  </span>
                </td>

                <td className="px-4 py-3">
                  <span className="font-medium text-gray-900">
                    {r.name || "-"}
                  </span>
                </td>

                <td className="px-4 py-3 text-center">
                  <span className="text-sm text-gray-700">
                    {r.team_name || "-"}
                  </span>
                </td>

                <td className="px-4 py-3 text-center">
                  <NumberChip value={r.team_id} />
                </td>

                <td className="px-4 py-3 text-center">
                  <ConfigBadge
                    color={
                      r.driver_type === "full_time"
                        ? "blue"
                        : r.driver_type === "part_time"
                          ? "purple"
                          : "gray"
                    }
                  >
                    {r.driver_type ? r.driver_type.replace("_", " ") : "-"}
                  </ConfigBadge>
                </td>

                <td className="px-4 py-3 text-center">
                  <NumberChip value={r.tier_level} />
                </td>

                <td className="px-4 py-3 text-center">
                  <ConfigBadge
                    color={
                      r.kyc_status === "A"
                        ? "green"
                        : r.kyc_status === "R"
                          ? "red"
                          : "yellow"
                    }
                  >
                    {r.kyc_status === "A"
                      ? "Approved"
                      : r.kyc_status === "R"
                        ? "Rejected"
                        : "Pending"}
                  </ConfigBadge>
                </td>

                <td className="px-4 py-3 text-center">
                  <ConfigBadge color={r.on_duty === 1 ? "green" : "red"}>
                    {r.on_duty === 1 ? "Yes" : "No"}
                  </ConfigBadge>
                </td>

                <td className="px-4 py-3 text-center">
                  <NumberChip value={r.cash_limit} prefix="₹" />
                </td>

                <td className="px-4 py-3 text-center">
                  <NumberChip value={r.daily_task_limit} />
                </td>

                <td className="px-4 py-3 text-center">
                  <NumberChip value={r.concurrent_order} />
                </td>

                <td className="px-4 py-3 text-center">
                  <NumberChip value={r.max_team_limit} />
                </td>

                <td className="px-4 py-3 text-center">
                  <NumberChip value={r.per_day_amount} prefix="₹" />
                </td>

                <td className="px-4 py-3 text-center">
                  <NumberChip value={r.per_order_amount} prefix="₹" />
                </td>

                <td className="px-4 py-3 text-center">
                  <span className="text-xs text-white">
                    {formatDate(r.created_on)}
                  </span>
                </td>

                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1.5">
                    <button
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-600 transition hover:bg-blue-50 hover:text-blue-700"
                      onClick={() => onEdit?.(r.driver_code)}
                      title="Edit Config"
                    >
                      <Pen size={14} />
                    </button>

                    <button
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-600 transition hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                      onClick={() => onDelete?.(r.pk_config_id)}
                      disabled={deletingId === String(r.pk_config_id)}
                      title="Delete Config"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {!rows?.length && (
              <tr>
                <td
                  className="px-4 py-8 text-center text-sm text-white"
                  colSpan={17}
                >
                  No configurations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
